import { plural, t } from '@lingui/core/macro';
import type { BookingUpdateOfferTierThresholdSecondsInput } from 'noddi-util';

const formatExpansionPartLocalized = (seconds: number, suffix: 'total' | 'earlier' | 'later'): string | null => {
  if (seconds <= 0) {
    return null;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    if (suffix === 'later') {
      return t`${hours} h ${minutes} min or later`;
    }
    if (suffix === 'earlier') {
      return t`${hours} h ${minutes} min earlier or more`;
    }
    return t`${hours} h ${minutes} min or more in total`;
  }

  if (hours > 0) {
    if (suffix === 'later') {
      return plural(hours, {
        one: '# hour or later',
        other: '# hours or later'
      });
    }
    if (suffix === 'earlier') {
      return plural(hours, {
        one: '# hour earlier or more',
        other: '# hours earlier or more'
      });
    }
    return plural(hours, {
      one: '# hour or more in total',
      other: '# hours or more in total'
    });
  }

  if (minutes > 0) {
    if (suffix === 'later') {
      return plural(minutes, {
        one: '# minute or later',
        other: '# minutes or later'
      });
    }
    if (suffix === 'earlier') {
      return plural(minutes, {
        one: '# minute earlier or more',
        other: '# minutes earlier or more'
      });
    }
    return plural(minutes, {
      one: '# minute or more in total',
      other: '# minutes or more in total'
    });
  }

  return null;
};

/** Customer-app tier labels; use instead of `formatBookingUpdateOfferTierThresholdSummary` from noddi-util. */
export const formatLocalizedBookingUpdateOfferTierThresholdSummary = (
  tier: BookingUpdateOfferTierThresholdSecondsInput
): string => {
  const parts: string[] = [];
  const total = formatExpansionPartLocalized(tier.minTotalExpansionSeconds, 'total');
  if (total != null) {
    parts.push(total);
  }
  if (tier.minExpansionEarlierSeconds != null && tier.minExpansionEarlierSeconds > 0) {
    const earlier = formatExpansionPartLocalized(tier.minExpansionEarlierSeconds, 'earlier');
    if (earlier != null) {
      parts.push(earlier);
    }
  }
  if (tier.minExpansionLaterSeconds != null && tier.minExpansionLaterSeconds > 0) {
    const later = formatExpansionPartLocalized(tier.minExpansionLaterSeconds, 'later');
    if (later != null) {
      parts.push(later);
    }
  }
  return parts.length > 0 ? parts.join(', ') : t`—`;
};
